import { useMemo } from 'react';
import { ArrowDownTrayIcon, FlagIcon } from '@heroicons/react/24/outline';
import { ArrowLeftIcon, ChatBubbleOvalLeftEllipsisIcon, InformationCircleIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'next-i18next';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { cn, toDateFormat, useAnalytics } from '@restworld/utils-common';
import { Alert } from '@restworld/ui-components';
import { Avatar, Tooltip, Link, Heading } from '@restworld/ui-ds-base';
import {
  candidateModalVariantAtom,
  EMPTY_REPORT_DRAFT,
  reportDraftAtom,
  reportOriginVariantAtom,
  selectedJobApplicationAtom,
} from '../../../employer-job-listing.atoms';
import { useKanbanRowsAndMatchStatusKeys } from '../../../hooks';
import { useJobApplicationReport } from '../../../hooks/use-job-application-report';
import { getJobApplicationId, isReportableJobApplication } from '../../../candidate-visibility';

// Variants of the candidate view where the "Report" link can appear.
const REPORT_ORIGIN_VARIANTS = ['default', 'worker_jp_discarded', 'spontaneous_candidate'];

interface CandidateModalHeaderProps {
  email?: string;
  image?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  cvLink?: string;
}

function CandidateModalHeader({ image, firstName, lastName, cvLink }: CandidateModalHeaderProps) {
  const { t } = useTranslation();
  const [variant, setVariant] = useAtom(candidateModalVariantAtom);
  const { trackEvent } = useAnalytics();
  const selectedJobApplication = useAtomValue(selectedJobApplicationAtom);
  const kanbanRowsAndMatchStatusKeys = useKanbanRowsAndMatchStatusKeys();

  const currentStatus = useMemo(
    () => kanbanRowsAndMatchStatusKeys.find((row) => row.matchStatusKey.includes(selectedJobApplication.status_key)),
    [kanbanRowsAndMatchStatusKeys, selectedJobApplication.status_key]
  );

  const setReportOriginVariant = useSetAtom(reportOriginVariantAtom);
  const setReportDraft = useSetAtom(reportDraftAtom);
  const isReportOrigin = REPORT_ORIGIN_VARIANTS.includes(variant);
  const isReportable = isReportableJobApplication(selectedJobApplication);
  const jobApplicationId = getJobApplicationId(selectedJobApplication);
  // Loaded on every candidate view, not only on reportable ones: an application reported and then
  // moved back (e.g. to the shortlist) must keep showing "Report sent", as the backend allows.
  const {
    data: report,
    isLoading: isReportLoading,
    isFetching: isReportFetching,
    isError: isReportError,
  } = useJobApplicationReport(jobApplicationId, isReportOrigin);
  // No control while the answer is unknown (nothing loaded yet: `undefined`, as opposed to a known
  // `null`) or when the read failed (401/404/network), so a stale "Report" link is never clickable.
  // A refetch of a known answer keeps the control in place, with no flicker; after a send or a 409
  // the "sent" state is written into the cache before refetching (use-report-candidate.tsx).
  const reportState: 'hidden' | 'sent' | 'can_report' =
    !isReportOrigin || isReportLoading || isReportError || (isReportFetching && report === undefined)
      ? 'hidden'
      : report
      ? 'sent'
      : isReportable
      ? 'can_report'
      : 'hidden';

  if (variant === 'references') {
    return (
      <div className="pt-3 lg:pt-0">
        <div className="flex justify-between items-center mb-3 pb-1">
          <Heading size={5} weight="medium" color="neutral">
            {t('common:references')}
          </Heading>
          <Link onClick={() => setVariant('default')} StartIcon={ArrowLeftIcon} newTab={false}>
            {t('job-positions-page:back_to_candidate')}
          </Link>
        </div>
        <Alert
          className="p-3 bg-green-50 mb-6 lg:mb-0"
          text={t('restworld_verifies_references_by_directly_contacting_the_listed_venues_key')}
          textClassName="text-base font-normal leading-6"
          variant="success"
          icon={<InformationCircleIcon className="h-4 w-4 min-h-[16px] min-w-[16px] text-green-700" />}
        />
      </div>
    );
  }

  if (variant === 'notes' || variant === 'spontaneous_candidate_notes') {
    return (
      <div>
        <div className="flex justify-between items-center mb-3">
          <Heading size={5} weight="medium" color="neutral">
            {t('job-positions-page:comments')}
          </Heading>
          <Link
            onClick={() => setVariant(variant === 'spontaneous_candidate_notes' ? 'spontaneous_candidate' : 'default')}
            StartIcon={ArrowLeftIcon}
            newTab={false}
          >
            {t('job-positions-page:back_to_candidate')}
          </Link>
        </div>
        <Alert
          className="p-3 w-fit bg-green-50"
          text={
            <span>
              {t('job-positions-page:your_comments_are')}{' '}
              <strong className="font-bold">{t('job-positions-page:private')}</strong>{' '}
              {t('job-positions-page:and_they_will_be')}{' '}
              <strong>{t('job-positions-page:visible_to_only_your_team')}.</strong> <br />
              {t('job-positions-page:neither_your_restworld_manager_nor_the_candidate_will_be_able_to_read_them')}.
            </span>
          }
          textClassName="text-base font-normal leading-6"
          variant="success"
          icon={<InformationCircleIcon className="h-4 w-4 min-h-[16px] min-w-[16px] text-green-700" />}
        />
      </div>
    );
  }

  return (
    <>
      <Wrapper>
        <InfoWrapper>
          <Avatar src={image} firstName={firstName} lastName={lastName} size="lg" />
        </InfoWrapper>

        <CtaWrapper>
          <Link
            StartIcon={ChatBubbleOvalLeftEllipsisIcon}
            newTab={false}
            onClick={() => setVariant(variant === 'spontaneous_candidate' ? 'spontaneous_candidate_notes' : 'notes')}
          >
            {t('common:your_comments')}
          </Link>
          {!cvLink ? (
            <Tooltip content={t('common:cv_not_available_tooltip')} placement="bottom">
              <Link disabled StartIcon={ArrowDownTrayIcon} newTab={false}>
                {t('common:download_cv')}
              </Link>
            </Tooltip>
          ) : (
            <Link
              href={cvLink}
              StartIcon={ArrowDownTrayIcon}
              newTab={false}
              onClick={() => trackEvent('employer_download_CV', { stage: currentStatus?.title })}
            >
              {t('common:download_cv')}
            </Link>
          )}
          {reportState !== 'hidden' ? (
            report ? (
              <Tooltip content={t('job-positions-page:report.already_sent_tooltip')} placement="bottom">
                <Link disabled StartIcon={FlagIcon} newTab={false}>
                  {t('job-positions-page:report.sent_on', { date: toDateFormat(report.inserted_at, 'dd/MM') })}
                </Link>
              </Tooltip>
            ) : (
              <Link
                StartIcon={FlagIcon}
                newTab={false}
                onClick={() => {
                  setReportOriginVariant(variant);
                  setReportDraft(EMPTY_REPORT_DRAFT);
                  setVariant('report');
                  trackEvent('employer_report_worker_opened', {
                    job_application_id: jobApplicationId,
                    stage: currentStatus?.title,
                    is_spontaneous: !selectedJobApplication.job_position_id,
                  });
                }}
              >
                {t('job-positions-page:report.link')}
              </Link>
            )
          ) : null}
        </CtaWrapper>
      </Wrapper>
    </>
  );
}

export default CandidateModalHeader;

const Wrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn('min-h-[88px] mb-4 flex justify-between items-center mt-0 lg:mt-0 lg:mb-8', className)}
    {...props}
  />
);
const InfoWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex lg:flex-row lg:gap-4', className)} {...props} />
);
const CtaWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('text-neutral-700 flex flex-col items-end', className)} {...props} />
);
